// appService.ts

import { initializeFileTypeIcons } from "@uifabric/file-type-icons"
import axios from "axios"
import qs from "qs"
import paletteEngine from "src/pages/dynamic_forms/Utils/paletteEngine"
import store from "src/store/store"
import actions from "src/store/UserReference/actions"
import { setFeatures } from "src/store/features/actions"
import { LOGIN_WITH_USERNAME_PASSWORD } from "src/store/user/types"
import stateService from "./utilities/AsyncStateManager/StateService"
import AxiosConfig from "./utilities/AxiosConfig"
import msalAuth from "./utilities/msalAuth"
import notificationStore from "./utilities/Notifications/notificationStore"

interface AuthTokenResponseTypes {
	access_token: string
	token_type: string
	expires_in: number
	userId: number
	userName: string
	sessionKey: string
	homeProfitCenter: number
	roles: string
	roles_and_assignments: string
	issued: string
	expires: string
	".expires": string
}

const appService = {
	rolesAndAssignments: [
		{ View: false, Edit: false, Assignments: [] as string[] },
	],
	microsoftToken: "",
	sessionKey: "",
	axiosHeaders: {
		headers: {
			Authorization: "Bearer ",
		},
	},

	setUpApp: () => {
		initializeFileTypeIcons()
		const palette = store.getState().dynamicFormPalette
		paletteEngine.buildHashTable(palette)
	},

	authenticateWithPortalAPI: (microsoftToken: string, history: any) => {
		const { updateState } = stateService

		updateState("appState", [{ key: "loggingIn", value: true }])

		const miniConfig = {
			headers: {
				"content-type": "application/x-www-form-urlencoded",
			},
		}

		const rb = {
			userName: `auth::${microsoftToken}`,
			password: "",
			grant_type: "password",
			client_id: process.env.REACT_APP_PORTAL_API_CLIENT_ID,
		}

		axios
			.post(
				`${process.env.REACT_APP_API_URL}/token`,
				qs.stringify(rb),
				miniConfig,
			)
			.then((res) => {
				const authToken: AuthTokenResponseTypes = res.data
				const {
					token_type,
					access_token,
					userName,
					roles,
					roles_and_assignments,
					sessionKey,
					userId,
					expires_in,
					issued,
					homeProfitCenter,
				} = authToken
				const config = AxiosConfig.setupConfig(
					token_type,
					access_token,
					sessionKey,
				)
				appService.microsoftToken = microsoftToken

				axios
					.get(`${process.env.REACT_APP_API_URL}/api/settings`, config)
					.then((res2: any) => {
						const settings: any = res2.data
						msalAuth.Settings = settings
						appService.rolesAndAssignments = JSON.parse(roles_and_assignments)
						appService.sessionKey = sessionKey
						appService.axiosHeaders = {
							headers: { Authorization: `Bearer ${microsoftToken}` },
						}
						updateState("appState", [
							{ key: "authenticated", value: true },
							{ key: "loggingIn", value: false },
						])
						const reduxPayload = {
							access_token: access_token,
							token_type: token_type,
							expires_in: expires_in,
							userName: userName,
							".issued": issued,
							".expires": authToken[".expires"],
							roles: roles.split(","),
							PortalSettings: settings.PortalSettings,
							UserSettings: settings.UserSettings,
							userId: userId,
							homeProfitCenter: homeProfitCenter,
						}

						store.dispatch({
							type: LOGIN_WITH_USERNAME_PASSWORD,
							payload: reduxPayload,
						})
						notificationStore.init(60000, userId)

						appService.loadFeatureFlags().then(() => {
							appService.handleRedirect(settings, history)
						})
					})
			})
			.then(() => {
				actions.auditReference()
			})
			.catch((err) => {
				console.error(err)
				localStorage.clear()
				updateState("appState", [{ key: "loggingIn", value: false }])
			})
	},
	reAuthenticateWithPortalAPI: async () => {
		const miniConfig = {
			headers: {
				"content-type": "application/x-www-form-urlencoded",
			},
		}

		const rb = {
			userName: `auth::${appService.microsoftToken}`,
			password: "",
			grant_type: "password",
			client_id: process.env.REACT_APP_PORTAL_API_CLIENT_ID,
		}

		try {
			const res = await axios.post(
				`${process.env.REACT_APP_API_URL}/token`,
				qs.stringify(rb),
				miniConfig,
			)
			const authToken: AuthTokenResponseTypes = res.data
			const {
				token_type,
				access_token,
				userName,
				roles,
				roles_and_assignments,
				sessionKey,
				userId,
				expires_in,
				issued,
				homeProfitCenter,
			} = authToken
			const config = AxiosConfig.setupConfig(
				token_type,
				access_token,
				sessionKey,
			)

			const res2 = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/settings`,
				config,
			)
			const settings: any = res2.data
			msalAuth.Settings = settings
			appService.rolesAndAssignments = JSON.parse(roles_and_assignments)
			appService.sessionKey = sessionKey
			appService.axiosHeaders = {
				headers: { Authorization: `Bearer ${appService.microsoftToken}` },
			}

			const reduxPayload = {
				access_token: access_token,
				token_type: token_type,
				expires_in: expires_in,
				userName: userName,
				".issued": issued,
				".expires": authToken[".expires"],
				roles: roles.split(","),
				PortalSettings: settings.PortalSettings,
				UserSettings: settings.UserSettings,
				userId: userId,
				homeProfitCenter: homeProfitCenter,
			}

			store.dispatch({
				type: LOGIN_WITH_USERNAME_PASSWORD,
				payload: reduxPayload,
			})
			await appService.loadFeatureFlags()
		} catch (err) {
			console.error(err)
		}
	},

	loadFeatureFlags: () => {
		return axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/features`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				store.dispatch(setFeatures(res.data))
			})
			.catch((err) => {
				AxiosConfig.handleError(err)
			})
	},

	clearSession: () => {
		const { updateState } = stateService
		notificationStore.killNotifications()
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/user/signout`,
				{},
				AxiosConfig.configValue(),
			)
			.then(() => {
				updateState("appState", [{ key: "authenticated", value: false }])
			})
			.catch((err) => {
				console.error(err)
			})
	},
	getPermissions: (targets: string | string[]) => {
		if (typeof targets === "string") {
			targets = [targets]
		}

		for (const role of appService.rolesAndAssignments) {
			if (targets.some((target) => role.Assignments.includes(target))) {
				return {
					canView: role.View,
					canEdit: role.Edit,
					hasRole: true,
				}
			}
		}
		return {
			canView: false,
			canEdit: false,
			hasRole: false,
		}
	},
	handleRedirect: (settings: any, history: any) => {
		if (
			history.location.pathname === "/" &&
			settings.UserSettings &&
			Object.hasOwn(settings.UserSettings, "defaultRedirect")
		) {
			const redirectUrl = settings.UserSettings.defaultRedirect
			history.push(redirectUrl)
		}
	},
}

export default appService
